export type TimeRangeKey = "30d" | "90d" | "6m" | "12m" | "all";
export type Granularity = "weekly" | "monthly";

export type StatsFilter = {
  range: TimeRangeKey;
  granularity: Granularity;
};

export type TrendBucket = {
  startInclusive: number;
  endExclusive: number;
};

export const TIME_RANGE_OPTIONS: ReadonlyArray<{
  key: TimeRangeKey;
  label: string;
}> = [
  { key: "30d", label: "30 days" },
  { key: "90d", label: "3 months" },
  { key: "6m", label: "6 months" },
  { key: "12m", label: "12 months" },
  { key: "all", label: "All time" },
];

export const GRANULARITY_OPTIONS: ReadonlyArray<{
  key: Granularity;
  label: string;
}> = [
  { key: "weekly", label: "Weekly" },
  { key: "monthly", label: "Monthly" },
];

export const DEFAULT_STATS_FILTER: StatsFilter = {
  range: "90d",
  granularity: "weekly",
};

const DAY_MS = 24 * 60 * 60 * 1000;
const WEEK_MS = 7 * DAY_MS;

const MAX_BUCKETS = 60;

export const parseTimeRangeKey = (value: string | null): TimeRangeKey | null =>
  TIME_RANGE_OPTIONS.find((o) => o.key === value)?.key ?? null;

export const parseGranularity = (value: string | null): Granularity | null =>
  GRANULARITY_OPTIONS.find((o) => o.key === value)?.key ?? null;

/**
 * Subtract whole calendar months, clamping the day of month so 31 Mar − 1 month
 * is 28/29 Feb rather than rolling forward into March as `setMonth` would.
 */
const subtractMonths = (from: number, months: number): number => {
  const d = new Date(from);
  const target = new Date(d.getFullYear(), d.getMonth() - months, 1);
  const daysInTarget = new Date(
    target.getFullYear(),
    target.getMonth() + 1,
    0,
  ).getDate();
  target.setDate(Math.min(d.getDate(), daysInTarget));
  target.setHours(d.getHours(), d.getMinutes(), d.getSeconds(), 0);
  return target.getTime();
};

const startOfMonth = (at: number): number => {
  const d = new Date(at);
  return new Date(d.getFullYear(), d.getMonth(), 1).getTime();
};

export const getRangeStart = (
  range: TimeRangeKey,
  now: number,
): number | null => {
  switch (range) {
    case "30d":
      return now - 30 * DAY_MS;
    case "90d":
      return now - 90 * DAY_MS;
    case "6m":
      return subtractMonths(now, 6);
    case "12m":
      return subtractMonths(now, 12);
    case "all":
      return null;
  }
};

export const isWithinRange = (
  at: string | number,
  rangeStart: number | null,
): boolean => {
  if (rangeStart === null) return true;
  const t = typeof at === "number" ? at : new Date(at).getTime();
  return Number.isFinite(t) && t >= rangeStart;
};

export const getTrendBuckets = (
  filter: StatsFilter,
  now: number,
  earliest: number | null,
): TrendBucket[] => {
  const start =
    getRangeStart(filter.range, now) ?? earliest ?? subtractMonths(now, 12);
  if (start >= now) return [];

  const buckets: TrendBucket[] = [];
  let end = now;

  while (end > start && buckets.length < MAX_BUCKETS) {
    const previous =
      filter.granularity === "weekly" ? end - WEEK_MS : startOfMonth(end - 1);
    const bucketStart = Math.max(start, previous);
    buckets.unshift({ startInclusive: bucketStart, endExclusive: end });
    end = bucketStart;
  }

  return buckets;
};

export const formatRangeLabel = (
  range: TimeRangeKey,
  now: number,
): string | null => {
  const start = getRangeStart(range, now);
  if (start === null) return null;
  const format = (at: number) =>
    new Date(at).toLocaleDateString([], {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  return `${format(start)} – ${format(now)}`;
};
